import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TxtFileParserService {
  public parseTxtFile(fileBuffer: any, file_name: string, athlete_id: number, weight: number, callback: any, sport?: any): void {
    try {
      let fileContent: string;
      if (typeof fileBuffer === 'string') {
        fileContent = fileBuffer;
      } else if (fileBuffer instanceof ArrayBuffer) {
        const decoder = new TextDecoder('utf-8');
        fileContent = decoder.decode(fileBuffer);
      } else if (fileBuffer instanceof Uint8Array) {
        const decoder = new TextDecoder('utf-8');
        fileContent = decoder.decode(fileBuffer);
      } else {
        fileContent = String(fileBuffer);
      }

      const lines = fileContent.split('\n').map(line => line.trim()).filter(line => line.length > 0);

      if (lines.length < 3) {
        callback({ error: 'Invalid TXT file format - insufficient data' });
        return;
      }

      const header = this.parseTxtHeader(lines);
      const dataPoints = this.parseTxtDataPoints(lines);

      if (dataPoints.length === 0) {
        callback({ error: 'No data points found in TXT file' });
        return;
      }

      const items = this.convertTxtToStandardFormat(dataPoints, header);

      const model = {
        file_name: file_name,
        initials: 'TXTParser',
        athlete: athlete_id,
        weight: weight,
        date: header.date ? new Date(header.date) : new Date(),
        time: this.last(items, 4),
        dist: this.last(items, 3),
        energy: this.calculateTxtEnergy(dataPoints) / 1000,
        power: this.average(items, 8),
        hr: this.average(items, 5),
        cad: this.average(items, 2),
        speed: this.average(items, 9),
        ascent: 0,
        json_data: JSON.stringify(items),
        time_zone: 0,
      };

      callback(model);

    } catch (error) {
      console.error('TXT parsing error:', error);
      callback({ error: `Failed to parse TXT file: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  }

  private parseTxtHeader(lines: string[]): any {
    const header: any = {
      sport: 'cycling',
      weight: '',
      date: '',
      height: ''
    };

    if (lines[0]) {
      const firstLine = lines[0];

      const dateMatch = firstLine.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
      if (dateMatch) {
        const dateParts = dateMatch[1].split('/');
        if (dateParts.length === 3) {
          const day = dateParts[0].padStart(2, '0');
          const month = dateParts[1].padStart(2, '0');
          const year = dateParts[2];
          header.date = `${year}-${month}-${day}`;
        }
      }

      const weightMatch = firstLine.match(/(\d+[,.]?\d*)kg/);
      if (weightMatch) {
        header.weight = weightMatch[1].replace(',', '.');
      }
    }

    if (lines[1] && lines[1].includes('T')) {
      header.date = lines[1].split('\t')[0] || header.date;
    }

    return header;
  }

  private parseTxtDataPoints(lines: string[]): any[] {
    const dataPoints: any[] = [];
    let headerRowIndex = -1;

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('Time') && lines[i].includes('Distance') && lines[i].includes('Power')) {
        headerRowIndex = i;
        break;
      }
    }

    if (headerRowIndex === -1) {
      throw new Error('Could not find data header row');
    }

    const dataStartIndex = headerRowIndex + 2;

    for (let i = dataStartIndex; i < lines.length; i++) {
      const line = lines[i];
      if (!line || line.trim() === '') continue;

      const dataPoint = this.parseTxtDataLine(line);
      if (dataPoint) {
        dataPoints.push(dataPoint);
      }
    }

    return dataPoints;
  }

  private parseTxtDataLine(line: string): any | null {
    const values = line.split(/\t|\s{2,}/).filter(v => v.trim() !== '');

    if (values.length < 6) {
      return null;
    }

    try {
      return {
        time: parseFloat(values[0].replace(',', '.')),
        distance: parseFloat(values[1].replace(',', '.')),
        power: parseFloat(values[2].replace(',', '.')),
        heart_rate: parseFloat(values[3].replace(',', '.')),
        cadence: parseFloat(values[4].replace(',', '.')),
        speed: parseFloat(values[5].replace(',', '.'))
      };
    } catch (error) {
      console.warn('Failed to parse data line:', line, error);
      return null;
    }
  }

  private convertTxtToStandardFormat(dataPoints: any[], header: any): any[] {
    const sessionStartDate = header.date ? new Date(header.date) : new Date();

    return dataPoints.map((point) => {
      const timestamp = new Date(sessionStartDate.getTime() + (point.time * 1000));

      return [
        timestamp.getTime() / 1000,
        0,
        0,
        point.distance,
        point.time,
        point.heart_rate,
        0,
        0,
        point.power,
        point.speed,
        point.cadence,
      ];
    });
  }

  private calculateTxtEnergy(dataPoints: any[]): number {
    let totalEnergy = 0;

    for (let i = 1; i < dataPoints.length; i++) {
      const current = dataPoints[i];
      const previous = dataPoints[i - 1];
      const timeDiff = current.time - previous.time;
      const avgPower = (current.power + previous.power) / 2;

      totalEnergy += avgPower * timeDiff;
    }

    return totalEnergy;
  }

  private last(items: any[], columnIndex: number): number {
    if (!items || items.length === 0) {
      return 0;
    }

    const lastItem = items[items.length - 1];
    if (!lastItem || !Array.isArray(lastItem) || lastItem.length <= columnIndex) {
      return 0;
    }

    const value = lastItem[columnIndex];
    return isNaN(value) ? 0 : Number(value);
  }

  private average(items: any[], columnIndex: number): number {
    if (!items || items.length === 0) {
      return 0;
    }

    let sum = 0;
    let count = 0;

    for (const item of items) {
      if (Array.isArray(item) && item.length > columnIndex) {
        const value = Number(item[columnIndex]);
        if (!isNaN(value) && value > 0) {
          sum += value;
          count++;
        }
      }
    }

    return count > 0 ? Math.round((sum / count) * 100) / 100 : 0;
  }
}
